import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Sparkles } from "lucide-react";

const TARIFS = [
  {
    badge: null,
    title: "Séance individuelle",
    price: "70 €",
    duration: "1h à 1h15",
    desc: "Une séance personnalisée pour avancer concrètement sur vos objectifs mentaux.",
    points: [
      "Visio ou présentiel",
      "Bilan & objectifs co-construits",
      "Outils repartis chez vous",
    ],
    note: "Tarif réduit 60 € pour étudiants & demandeurs d'emploi",
    accent: "primary",
  },
  {
    badge: "Sur-mesure",
    title: "Forfaits, groupes & ateliers",
    price: "Sur devis",
    duration: "À partir de 2h",
    desc: "Forfaits longue durée, ateliers en équipe sportive, club ou entreprise — chaque format est calibré selon vos besoins.",
    points: [
      "Accompagnements 3, 6 ou 12 mois",
      "Interventions en clubs / entreprises",
      "Tarifs dégressifs selon le volume",
    ],
    note: "Échangeons sur votre projet pour construire un devis adapté.",
    accent: "secondary",
  },
];

export const Tarifs = () => {
  return (
    <section id="tarifs" className="relative py-24 md:py-32 bg-[#FAF6F1]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#9B8FE0]">
            Tarifs
          </span>
          <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            Une base claire, des <span className="text-[#9B8FE0]">forfaits</span> sur-mesure.
          </h2>
          <p className="mt-6 text-xl md:text-2xl text-zinc-600 leading-relaxed max-w-2xl">
            Le tarif horaire fixe la base pour les séances individuelles. Pour
            les groupes, équipes et accompagnements longs, on construit
            ensemble un devis adapté à vos objectifs.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {TARIFS.map((t, i) => (
            <motion.article
              key={t.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`tarif-card-${i}`}
              className={`relative rounded-3xl p-8 md:p-10 border transition-all overflow-hidden ${
                t.accent === "primary"
                  ? "bg-gradient-to-br from-[#9B8FE0] to-[#B5A8EC] text-white border-transparent shadow-[0_20px_60px_-20px_rgba(155,143,224,0.55)]"
                  : "bg-white border-zinc-200 hover:border-[#F4B8C7]/70 hover:shadow-[0_18px_50px_-20px_rgba(244,184,199,0.5)]"
              }`}
            >
              <div className="flex items-center gap-2">
                {t.badge && (
                <span
                  className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${
                    t.accent === "primary"
                      ? "bg-white/20 text-white"
                      : "bg-[#F4B8C7]/30 text-[#A04F66]"
                  }`}
                >
                  <Sparkles size={12} /> {t.badge}
                </span>
                )}
              </div>

              <h3 className={`font-display mt-5 text-3xl md:text-4xl font-bold tracking-tight ${t.accent === "primary" ? "text-white" : "text-zinc-900"}`}>
                {t.title}
              </h3>

              <div className="mt-6 flex items-baseline gap-3">
                <span className={`font-display text-2xl md:text-3xl font-black ${t.accent === "primary" ? "text-white" : "text-zinc-900"}`}>
                  {t.price}
                </span>
                <span className={`text-base md:text-lg ${t.accent === "primary" ? "text-white/85" : "text-zinc-500"}`}>
                  · {t.duration}
                </span>
              </div>

              <p className={`mt-5 text-lg md:text-xl leading-relaxed ${t.accent === "primary" ? "text-white/95" : "text-zinc-600"}`}>
                {t.desc}
              </p>

              <ul className="mt-7 space-y-3">
                {t.points.map((p) => (
                  <li
                    key={p}
                    className={`flex items-start gap-3 text-base md:text-lg ${t.accent === "primary" ? "text-white/95" : "text-zinc-700"}`}
                  >
                    <span
                      className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                        t.accent === "primary" ? "bg-white/20" : "bg-[#9B8FE0]/15"
                      }`}
                    >
                      <Check size={14} className={t.accent === "primary" ? "text-white" : "text-[#9B8FE0]"} />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>

              <div className={`mt-8 pt-6 border-t text-base md:text-lg leading-relaxed ${t.accent === "primary" ? "border-white/25 text-white/95" : "border-zinc-200 text-zinc-600"}`}>
                {t.note}
              </div>

              <a
                href="#contact"
                data-testid={`tarif-cta-${i}`}
                className={`mt-7 inline-flex items-center gap-2 font-bold text-base md:text-lg rounded-full px-7 py-3.5 transition-all ${
                  t.accent === "primary"
                    ? "bg-white text-[#9B8FE0] hover:-translate-y-0.5 hover:shadow-lg"
                    : "bg-zinc-900 text-white hover:bg-[#9B8FE0]"
                }`}
              >
                {t.accent === "primary" ? "Réserver une séance" : "Demander un devis"} <ArrowRight size={16} />
              </a>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Tarifs;
